import { CalendarX2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Pencil component `FJ6yr` (`v2/EmptyState`).
 *
 * White card with subtle border. Centered:
 *   - 64×64 muted circle holding a Lucide icon (default `calendar-x-2`)
 *   - 16px semibold title
 *   - 13px muted description (max-width 300px so it wraps nicely)
 *   - Optional CTA (the Pencil reference shows "+ New booking")
 *
 * Two render modes:
 *   - Standalone: full card with border, sized for a page slot.
 *   - Inside a parent card / table (`bare`): no outer border / fill, so it
 *     stacks cleanly inside the existing chrome (used by `DataTable`).
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  bare,
  className,
}: {
  /** Lucide icon component. Defaults to `CalendarX2` to match Pencil. */
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: React.ReactNode;
  /** Render without the outer card chrome. Use inside an existing card / table. */
  bare?: boolean;
  className?: string;
}) {
  const Icon = icon ?? CalendarX2;
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center gap-4",
        bare
          ? "px-6 py-12"
          : "px-8 py-10 rounded-lg border bg-card",
        className,
      )}
    >
      <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
        <Icon className="h-7 w-7 text-muted-foreground" />
      </div>
      <div className="flex flex-col items-center gap-1.5">
        <h3 className="text-base font-semibold tracking-tight">{title}</h3>
        {description && (
          <p className="text-[13px] text-muted-foreground max-w-[300px] leading-relaxed">
            {description}
          </p>
        )}
      </div>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
